// src/pages/ContactPage.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaFacebookF, FaLinkedinIn, FaYoutube } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import FormField from '../components/common/FormField';
import { COMPANY_INFO } from '../utils/constants';
import useForm from '../hooks/useForm';
import { setupRevealAnimations } from '../utils/helpers';

const ContactPage = () => {
  // Initialize form handling with custom hook
  const { values, errors, isSubmitting, isSubmitted, handleChange, handleSubmit, resetForm } = useForm(
    // Initial values
    {
      name: '',
      email: '',
      subject: '',
      message: ''
    },
    // Validation function
    (values) => {
      const errors = {};
      
      if (!values.name.trim()) {
        errors.name = 'Name is required';
      }
      
      if (!values.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        errors.email = 'Email is invalid';
      }
      
      if (!values.message.trim()) {
        errors.message = 'Message is required';
      }
      
      return errors;
    },
    // Submit function
    async (values) => {
      // This would normally be an API call
      console.log('Form submitted:', values);
      
      // Simulate API call
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1500);
      });
    }
  );
  
  // State for success message
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Show success message when form is submitted
  useEffect(() => {
    if (isSubmitted) {
      setShowSuccessMessage(true);
      
      // Hide success message after 5 seconds
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [isSubmitted]);
  
  useEffect(() => {
    // Initialize animations
    setupRevealAnimations();
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <>
      <Helmet>
        <title>Contact Us - Afrovate Tech Hub SL</title>
        <meta name="description" content="Get in touch with Leotech Sierra Leone. We'd love to hear from you!" />
      </Helmet>
      
      {/* Hero Section */}
      <Section background="gradient-primary" padding="lg" className="pt-32">
        <div className="text-center">
          <Heading level={1} color="white" align="center" className="mb-6">
            Contact Us
          </Heading>
          <p className="text-xl text-white text-opacity-90 max-w-3xl mx-auto">
            Have questions or want to learn more about our programs? We'd love to hear from you!
          </p>
        </div>
      </Section>
      
      {/* Contact Information and Form */}
      <Section background="white" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <div className="bg-primary-dark text-white p-8 rounded-lg shadow-xl h-full">
              <Heading level={3} color="white" className="mb-6">
                Get In Touch
              </Heading>
              
              <ul className="space-y-6">
                <motion.li 
                  className="flex items-start"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="mt-1 mr-4 bg-white bg-opacity-20 p-3 rounded-full">
                    <FaMapMarkerAlt className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Address</h4>
                    <p>{COMPANY_INFO.contact.address}</p>
                  </div>
                </motion.li>
                
                <motion.li 
                  className="flex items-start"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="mt-1 mr-4 bg-white bg-opacity-20 p-3 rounded-full">
                    <FaPhone className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Phone</h4>
                    <p>{COMPANY_INFO.contact.phone}</p>
                  </div>
                </motion.li>
                
                <motion.li 
                  className="flex items-start"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="mt-1 mr-4 bg-white bg-opacity-20 p-3 rounded-full">
                    <FaEnvelope className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Email</h4>
                    <p>
                      <a 
                        href={`mailto:${COMPANY_INFO.contact.email}`} 
                        className="hover:text-secondary transition-colors"
                      >
                        {COMPANY_INFO.contact.email}
                      </a>
                    </p>
                  </div>
                </motion.li>
              </ul>
              
              <div className="mt-10">
                <h4 className="font-bold text-lg mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  <a
                    href="https://www.facebook.com/LeoTECH2021.1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 p-3 rounded-full transition-colors"
                    aria-label="Facebook"
                  >
                    <FaFacebookF className="h-5 w-5" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/leotech-sl-ltd"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 p-3 rounded-full transition-colors"
                    aria-label="LinkedIn"
                  >
                    <FaLinkedinIn className="h-5 w-5" />
                  </a>
                  <a
                    href="https://youtube.com/@leotechsl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 p-3 rounded-full transition-colors"
                    aria-label="YouTube"
                  >
                    <FaYoutube className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-100">
              <Heading level={3} className="mb-6">
                Send Us a Message
              </Heading>
              
              {/* Success Message */}
              {showSuccessMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-100 border border-green-200 text-green-800 p-4 rounded-md mb-6"
                >
                  <p className="font-medium">Thank you for your message!</p>
                  <p>We have received your inquiry and will get back to you as soon as possible.</p>
                </motion.div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <FormField
                    label="Your Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                  />
                  <FormField
                    label="Email Address"
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                  />
                </div>
                
                <FormField
                  label="Subject"
                  name="subject"
                  value={values.subject}
                  onChange={handleChange}
                  error={errors.subject}
                  className="mb-6"
                />
                
                <FormField
                  label="Message"
                  name="message"
                  value={values.message}
                  onChange={handleChange}
                  error={errors.message}
                  type="textarea"
                  required
                  className="mb-6"
                  inputClassName="min-h-[150px]"
                />
                
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full md:w-auto"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </Section>
      
      {/* Google Maps */}
      <Section background="gray" padding="lg" className="text-center">
        <Heading level={2} align="center" className="mb-8" withAccent>
          Find Us
        </Heading>
        
        <div className="bg-white p-2 rounded-lg shadow-md max-w-5xl mx-auto">
          <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg flex items-center justify-center">
            <span className="text-gray-400">Google Maps will be implemented soon</span>
          </div>
        </div>
      </Section>
    </>
  );
};

export default ContactPage;